// F10: AI 依頼プロンプト生成（手順書「AI への依頼テンプレート」「作品要約シート」のフォーム化）。
// 2026-07-07 改定: 一般公開ツールとして内部資料（docs/schemas/本番 maps）への参照を全廃し、
// 依頼文 MD 単体で完結する自己完結型に変更 — 出力フォーマット仕様（ルール・対象ファイル・サンプル雛形）を
// story-pipeline.json の generate ユニット＋ツール同梱サンプル（data/maps/sample_town/）から依頼文へ直接埋め込む。
import path from 'node:path';
import { existsSync, readFileSync } from 'node:fs';
import { CONFIG_DIR, TOOLS_DIR } from './repo-paths.mjs';
import { readJson } from './json-utils.mjs';
import { pipelineConfig } from './story-shared.mjs';
import { auditLog } from './logger.mjs';

/** 出力フォーマット仕様セクションを組み立てる（⑤JSON 生成と同じ単一情報源＝仕様の二重管理をしない）。 */
function buildFormatSpec() {
    const gen = pipelineConfig().generate ?? {};
    const lines = [
        '## 出力フォーマット仕様（このセクションだけで完結・外部資料の参照は不要）',
        '',
        '開始ワールドの ID（以下 `<開始ワールドID>`）は英小文字 snake_case で命名すること（例: hoshikage_village）。',
        '',
        '### 共通ルール',
        ...(gen.commonRules ?? []).map((r) => `- ${r}`),
        '',
    ];
    for (const unit of gen.units ?? []) {
        const target = String(unit.target ?? '').replace('{startMapId}', '<開始ワールドID>');
        lines.push(`### ${unit.label}（\`${target}\`）`, '');
        for (const ins of unit.instructions ?? []) lines.push(`- ${ins}`);
        const exampleAbs = unit.exampleFile ? path.join(TOOLS_DIR, unit.exampleFile) : null;
        if (exampleAbs && existsSync(exampleAbs)) {
            lines.push('', 'サンプル（この構造・キー名に従うこと。値は作品に合わせて変える）:', '', '```json');
            lines.push(readFileSync(exampleAbs, 'utf-8').trim());
            lines.push('```');
        }
        lines.push('');
    }
    lines.push(
        '### 翻訳（ロケール JSON への追加キー）',
        '',
        '- ゲーム内の全文言（NPC 名・台詞・アイテム名・イベント文など）は、日本語と英語のロケールへ**同じキーで両方**追加すること。',
        '- 出力形式: `maps/_shared/i18n/locales/ja.json への追加キー` / 同 `en.json への追加キー` として、それぞれ次の形の JSON オブジェクトで出力する:',
        '',
        '```json',
        '{',
        '  "npc_guide_name": "灯台守ナギ",',
        '  "npc_guide_dialogue_1": "よく来たね。この港の灯は竜の火で灯っているんだよ。"',
        '}',
        '```',
        '',
    );
    return lines.join('\n');
}

/**
 * @param {object} input
 *   gameId: 任意（gameDir 表記に使用。未指定は <gameDir> のまま）
 *   summary: { title, protagonist, playable, maps, buildings, npcs, enemies, items, skills, events, audio, translation }
 */
export function buildAiRequest(input = {}) {
    const cfg = readJson(path.join(CONFIG_DIR, 'prompt-template.json'));
    const gameDir = input.gameId ? `newgames/${input.gameId}` : '<gameDir>';
    const requestText = [
        cfg.requestTemplate.join('\n').replaceAll('{gameDir}', gameDir),
        '',
        buildFormatSpec(),
    ].join('\n');

    const rows = cfg.summarySheetRows.map((row) => {
        const value = (input.summary?.[row.key] ?? '').toString().trim();
        return `| ${row.label} | ${value !== '' ? value.replaceAll('\n', '<br>') : `（未記入: ${row.hint}）`} |`;
    });
    const summarySheet = [
        '# 作品要約シート',
        '',
        '| 項目 | 内容 |',
        '|---|---|',
        ...rows,
        '',
    ].join('\n');

    auditLog('prompt-build', { gameId: input.gameId ?? null });
    return {
        requestText,
        summarySheet,
        attachments: [], // 内部資料の添付は廃止（依頼文が自己完結・2026-07-07）
        usage: [
            '1. 「📥 MD ファイルをダウンロード」で保存した依頼文（作品要約シート込み）と、小説本文を AI（ChatGPT / Gemini 等）へ渡す。渡すのはこの 2つだけで完結します。',
            '2. まず設計成果物（マップ分割表 / ID 一覧 / 配置設計表 / 翻訳表）だけを出させ、レビュー後に完全 JSON を生成させると精度が上がります。',
            '3. 出力された JSON は、ゲーム詳細ページの「取り込み」で配置する。',
        ],
    };
}
